import React from 'react';
import styles from './Explore.module.css';
import * as utils from '../../utils/utils';
import Sidebar from '../Sidebar/Sidebar';

const DEFAULT_TYPES = [
    'live_resume',
    'skill',
    'domain',
    'tool',
    'soft_skill',
    'certificate',
    'career_category',
    'experience',
    'education',
    'course',
    'skill_gap',
    'tool_gap',
    'domain_gap',
    'soft_skill_gap',
    'certificate_gap',
    'achievement',
    'impact',
    'entity',
];

const formatLabel = (value) => {
    return value
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export default class Explore extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            errorText: '',
            queryText: '',
            isSearchMode: false,
            parsedQuery: null,
            searchResults: [],

            isChatOpen: false,
            isChatLoading: false,
            isChatSending: false,
            chatErrorText: '',
            activeThread: null,
            activeTargetUser: null,
            chatMessages: [],
            chatInputText: '',

            selectedType: 'live_resume',
            connectionGroupMap: new Map(),
            availableTypes: [...DEFAULT_TYPES],
        };
    }

    componentDidMount() {
        if (!utils.isLoggedIn()) {
            window.location.href = '/signin';
            return;
        }

        this.loadConnections();
    }

    loadConnections = () => {
        this.setState({
            isLoading: true,
            errorText: '',
        }, async () => {
            try {
                const response = await utils.sendRequest('/userConnection/getUserConnections', {
                    offset: 0,
                    limit: 300,
                });

                const groups = Array.isArray(response.connectionGroups) ? response.connectionGroups : [];
                const connectionGroupMap = new Map();
                groups.forEach((group) => {
                    const key = group.connectionType;
                    const values = Array.isArray(group.connections) ? group.connections : [];
                    connectionGroupMap.set(key, values);
                });

                const availableTypeSet = new Set(DEFAULT_TYPES);
                groups.forEach((group) => {
                    if (typeof group.connectionType === 'string') {
                        availableTypeSet.add(group.connectionType);
                    }
                });

                const availableTypes = [...availableTypeSet];
                const selectedType = connectionGroupMap.has(this.state.selectedType)
                    ? this.state.selectedType
                    : (availableTypes[0] || 'live_resume');

                this.setState({
                    isLoading: false,
                    connectionGroupMap,
                    availableTypes,
                    selectedType,
                });
            } catch (error) {
                this.setState({
                    isLoading: false,
                    errorText: error && error.message ? error.message : 'Could not load user connections right now.',
                });
            }
        });
    };

    onTypeClick = (selectedType) => {
        this.setState({ selectedType });
    };

    onQueryTextChange = (event) => {
        this.setState({ queryText: event.target.value });
    };

    runNaturalLanguageSearch = () => {
        const query = this.state.queryText.trim();

        if (!query) {
            this.setState({
                errorText: 'Please type a query first.',
            });
            return;
        }

        this.setState({
            isLoading: true,
            errorText: '',
            isSearchMode: true,
        }, async () => {
            try {
                const response = await utils.sendRequest('/userConnection/searchByQuery', {
                    query,
                });

                this.setState({
                    isLoading: false,
                    parsedQuery: response.parsedQuery || null,
                    searchResults: Array.isArray(response.results) ? response.results : [],
                });
            } catch (error) {
                this.setState({
                    isLoading: false,
                    errorText: error && error.message ? error.message : 'Could not run semantic search right now.',
                });
            }
        });
    };

    resetSearchMode = () => {
        this.setState({
            isSearchMode: false,
            parsedQuery: null,
            searchResults: [],
            errorText: '',
        });
    };

    formatDateTime = (value) => {
        if (!value) {
            return 'N/A';
        }

        const dateValue = new Date(value);
        if (Number.isNaN(dateValue.getTime())) {
            return 'N/A';
        }

        return dateValue.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    formatScore = (value) => {
        const score = Number(value);

        if (!Number.isFinite(score)) {
            return '0.00';
        }

        return score.toFixed(2);
    };

    getSelectedConnections = () => {
        const { selectedType, connectionGroupMap } = this.state;
        return connectionGroupMap.get(selectedType) || [];
    };

    openChatWithUser = (targetUserID, fallbackFirstName, fallbackLastName) => {
        if (!targetUserID) {
            return;
        }

        this.setState({
            isChatOpen: true,
            isChatLoading: true,
            chatErrorText: '',
            chatMessages: [],
            activeThread: null,
            activeTargetUser: {
                userId: targetUserID,
                firstName: fallbackFirstName || '',
                lastName: fallbackLastName || '',
            },
        }, async () => {
            try {
                const threadResp = await utils.sendRequest('/message/startOrGetThread', {
                    targetUserID,
                });

                const thread = threadResp.userChatThread;
                const targetUser = threadResp.targetUser || {};

                const messagesResp = await utils.sendRequest('/message/listMessages', {
                    userChatThreadID: thread.userChatThreadId,
                    limit: 100,
                });

                this.setState({
                    isChatLoading: false,
                    activeThread: thread,
                    activeTargetUser: {
                        userId: targetUser.userId || targetUserID,
                        firstName: targetUser.firstName || fallbackFirstName || '',
                        lastName: targetUser.lastName || fallbackLastName || '',
                    },
                    chatMessages: Array.isArray(messagesResp.messages) ? messagesResp.messages : [],
                    chatErrorText: '',
                });
            } catch (error) {
                this.setState({
                    isChatLoading: false,
                    chatErrorText: error && error.message ? error.message : 'Could not open chat thread.',
                });
            }
        });
    };

    closeChat = () => {
        this.setState({
            isChatOpen: false,
            isChatLoading: false,
            isChatSending: false,
            chatErrorText: '',
            activeThread: null,
            activeTargetUser: null,
            chatMessages: [],
            chatInputText: '',
        });
    };

    onChatInputChange = (event) => {
        this.setState({
            chatInputText: event.target.value,
        });
    };

    sendChatMessage = () => {
        const { activeThread, chatInputText, isChatSending } = this.state;

        if (isChatSending || !activeThread || !activeThread.userChatThreadId) {
            return;
        }

        const messageText = chatInputText.trim();
        if (!messageText) {
            return;
        }

        this.setState({
            isChatSending: true,
            chatErrorText: '',
        }, async () => {
            try {
                const response = await utils.sendRequest('/message/send', {
                    userChatThreadID: activeThread.userChatThreadId,
                    messageText,
                });

                const sentMessage = response.message;

                this.setState((prevState) => ({
                    isChatSending: false,
                    chatInputText: '',
                    chatMessages: [...prevState.chatMessages, sentMessage],
                    activeThread: {
                        ...prevState.activeThread,
                        lastMessageAt: sentMessage.createdAt,
                    },
                }));
            } catch (error) {
                this.setState({
                    isChatSending: false,
                    chatErrorText: error && error.message ? error.message : 'Could not send message.',
                });
            }
        });
    };

    onChatInputKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendChatMessage();
        }
    };

    renderConnectionCard = (connection) => {
        const fullName = `${connection.targetFirstName || ''} ${connection.targetLastName || ''}`.trim() || 'Unknown User';

        return (
            <article key={`${connection.userConnectionId}-${connection.targetUserId}`} className={styles.connectionCard}>
                <div className={styles.connectionHeaderRow}>
                    <h3 className={styles.connectionName}>{fullName}</h3>
                    <span className={styles.scoreChip}>Score {this.formatScore(connection.relevanceScore)}</span>
                </div>

                <p className={styles.connectionMeta}>Target User ID: {connection.targetUserId}</p>
                <p className={styles.connectionMeta}>Recent Live Resume: {this.formatDateTime(connection.targetRecentLiveResumeAt)}</p>
                <p className={styles.connectionMeta}>Last Online: {this.formatDateTime(connection.targetLastOnline)}</p>

                <div className={styles.cardActionsRow}>
                    <button
                        className={styles.messageButton}
                        onClick={() => this.openChatWithUser(connection.targetUserId, connection.targetFirstName, connection.targetLastName)}
                    >
                        Message
                    </button>
                </div>
            </article>
        );
    };

    renderSearchResultCard = (result) => {
        const fullName = `${result.targetFirstName || ''} ${result.targetLastName || ''}`.trim() || 'Unknown User';
        const matchedOn = Array.isArray(result.matchedOn) ? result.matchedOn : [];

        return (
            <article key={`${result.userConnectionId}-${result.targetUserId}`} className={styles.connectionCard}>
                <div className={styles.connectionHeaderRow}>
                    <h3 className={styles.connectionName}>{fullName}</h3>
                    <span className={styles.scoreChip}>Score {this.formatScore(result.score)}</span>
                </div>

                <p className={styles.connectionMeta}>Target User ID: {result.targetUserId}</p>
                <p className={styles.connectionMeta}>Recent Live Resume: {this.formatDateTime(result.targetRecentLiveResumeAt)}</p>

                {
                    matchedOn.length > 0 &&
                    <div className={styles.matchTagWrap}>
                        {matchedOn.map((item) => (
                            <span key={`${result.targetUserId}-${item}`} className={styles.matchTag}>{item}</span>
                        ))}
                    </div>
                }

                {
                    result.evidenceSnippet &&
                    <p className={styles.evidenceText}>{result.evidenceSnippet}</p>
                }

                {
                    result.rankingBreakdown &&
                    <p className={styles.breakdownText}>
                        semantic {this.formatScore(result.rankingBreakdown.semanticScore)} | structured {this.formatScore(result.rankingBreakdown.structuredScore)} | recency {this.formatScore(result.rankingBreakdown.recencyScore)} | prior {this.formatScore(result.rankingBreakdown.priorScore)}
                    </p>
                }

                <div className={styles.cardActionsRow}>
                    <button
                        className={styles.messageButton}
                        onClick={() => this.openChatWithUser(result.targetUserId, result.targetFirstName, result.targetLastName)}
                    >
                        Message
                    </button>
                </div>
            </article>
        );
    };

    renderChatPanel = () => {
        const {
            isChatOpen,
            isChatLoading,
            isChatSending,
            chatErrorText,
            activeTargetUser,
            chatMessages,
            chatInputText,
        } = this.state;

        if (!isChatOpen) {
            return null;
        }

        const targetName = `${activeTargetUser?.firstName || ''} ${activeTargetUser?.lastName || ''}`.trim() || 'User';

        return (
            <aside className={styles.chatPanel}>
                <div className={styles.chatHeader}>
                    <h3 className={styles.chatTitle}>Chat with {targetName}</h3>
                    <button className={styles.chatCloseButton} onClick={this.closeChat}>Close</button>
                </div>

                {
                    isChatLoading &&
                    <div className={styles.chatStatusBox}>Opening thread...</div>
                }

                {
                    !isChatLoading && chatErrorText !== '' &&
                    <div className={styles.chatErrorBox}>{chatErrorText}</div>
                }

                {
                    !isChatLoading && chatErrorText === '' && chatMessages.length === 0 &&
                    <div className={styles.chatStatusBox}>No messages yet. Start the conversation.</div>
                }

                {
                    !isChatLoading && chatMessages.length > 0 &&
                    <div className={styles.chatMessageList}>
                        {chatMessages.map((item) => (
                            <div key={item.userChatMessageId} className={styles.chatMessageItem}>
                                <div className={styles.chatMessageMeta}>User {item.senderUserId} • {this.formatDateTime(item.createdAt)}</div>
                                <div className={styles.chatMessageText}>{item.messageText}</div>
                            </div>
                        ))}
                    </div>
                }

                <div className={styles.chatComposerRow}>
                    <textarea
                        className={styles.chatComposerInput}
                        value={chatInputText}
                        onChange={this.onChatInputChange}
                        onKeyDown={this.onChatInputKeyDown}
                        placeholder='Type your message...'
                    />

                    <button
                        className={styles.messageButton}
                        onClick={this.sendChatMessage}
                        disabled={isChatSending || isChatLoading}
                    >
                        {isChatSending ? 'Sending...' : 'Send'}
                    </button>
                </div>
            </aside>
        );
    };

    render() {
        const {
            isLoading,
            errorText,
            selectedType,
            availableTypes,
            queryText,
            isSearchMode,
            parsedQuery,
            searchResults,
        } = this.state;

        const selectedConnections = this.getSelectedConnections();

        return (
            <div className={styles.pageContainer}>
                <Sidebar />

                <div className={styles.contentShell}>
                    <header className={styles.header}>
                        <h1 className={styles.title}>Explore User Connections</h1>
                        <p className={styles.subtitle}>Connections are ranked using vector similarity and recency signals.</p>
                    </header>

                    <div className={styles.searchRow}>
                        <input
                            className={styles.searchInput}
                            value={queryText}
                            onChange={this.onQueryTextChange}
                            placeholder='Example: looking for someone who has been doing marketing work at school'
                        />
                        <button className={styles.searchButton} onClick={this.runNaturalLanguageSearch}>Ask</button>
                        {
                            isSearchMode &&
                            <button className={styles.secondaryButton} onClick={this.resetSearchMode}>Browse Mode</button>
                        }
                    </div>

                    {
                        isSearchMode && parsedQuery &&
                        <div className={styles.statusBox}>
                            Parsed query context: {parsedQuery.context}. Max results: {parsedQuery.maxResults}. Recency days: {parsedQuery.recencyDays}.
                        </div>
                    }

                    {
                        !isSearchMode &&
                        <div className={styles.typeTabsWrap}>
                            {availableTypes.map((typeName) => (
                                <button
                                    key={typeName}
                                    className={`${styles.typeTab} ${selectedType === typeName ? styles.typeTabActive : ''}`}
                                    onClick={() => this.onTypeClick(typeName)}
                                >
                                    {formatLabel(typeName)}
                                </button>
                            ))}
                        </div>
                    }

                    {
                        isLoading &&
                        <div className={styles.statusBox}>Loading user connections...</div>
                    }

                    {
                        !isLoading && errorText !== '' &&
                        <div className={styles.errorBox}>{errorText}</div>
                    }

                    {
                        !isLoading && errorText === '' && !isSearchMode && selectedConnections.length === 0 &&
                        <div className={styles.statusBox}>No matches available for this connection type yet.</div>
                    }

                    {
                        !isLoading && errorText === '' && isSearchMode && searchResults.length === 0 &&
                        <div className={styles.statusBox}>No semantic matches found for this query.</div>
                    }

                    {
                        !isLoading && errorText === '' && !isSearchMode && selectedConnections.length > 0 &&
                        <section className={styles.connectionsGrid}>
                            {selectedConnections.map(this.renderConnectionCard)}
                        </section>
                    }

                    {
                        !isLoading && errorText === '' && isSearchMode && searchResults.length > 0 &&
                        <section className={styles.connectionsGrid}>
                            {searchResults.map(this.renderSearchResultCard)}
                        </section>
                    }

                    {this.renderChatPanel()}
                </div>
            </div>
        );
    }
}
